import { useRef } from "react";
import type { KeyboardEvent, PointerEvent, ReactNode } from "react";
import { useI18n } from "../../../i18n";
import { SLIDER_STEP } from "../lib/input";
import { speak } from "../../../lib/speak";
import type { RemoteApi } from "../hooks/useRemote";
import type { TouchControl, Dpad } from "../types";
import "../../../styles/remote.css";

/**
 * FS-09 리모컨 오버레이 — 코스 장면 위(SCR v0.6 FS-09-01 SU 표준 · FS-09-02 SE 대형).
 *  - SU: 8방향 D-패드(56px) · 속도 슬라이더(-100~100, '속도 +70') · A(짙은)/B(흰) 72px
 *  - SE: 4방향 대형 D-패드 · 2단 속도(🐢 천천히 / 🐇 빨리) · A(노랑)/B(흰) — 모든 버튼 ≥96px(DR-03)
 *  - 멀티터치: 손가락(pointerId)마다 따로 누름/뗌(방향 + A/B 동시). 버튼을 벗어나도 뗄 때까지 누름(포인터 캡처).
 *  - SE 읽어 주기: 버튼을 길게 누르면 이름을 읽는다.
 * 자유 입력 없음(DR-02) — 슬라이더도 input 이 아니라 role=slider 요소다.
 */
/** 길게 누름 → 이름 읽기(FS-09-02 SE) */
const LONG_PRESS_MS = 600;

const SU_GRID: (Exclude<Dpad, null> | null)[] = ["nw", "n", "ne", "w", null, "e", "sw", "s", "se"];
const SE_GRID: (Exclude<Dpad, null> | null)[] = [null, "n", null, "w", null, "e", null, "s", null];
const GLYPH: Record<Exclude<Dpad, null>, { su: string; se: string }> = {
  n: { su: "▲", se: "▲" },
  s: { su: "▼", se: "▼" },
  w: { su: "◀", se: "◀" },
  e: { su: "▶", se: "▶" },
  nw: { su: "↖", se: "↖" },
  ne: { su: "↗", se: "↗" },
  sw: { su: "↙", se: "↙" },
  se: { su: "↘", se: "↘" },
};

function HoldButton({
  control,
  remote,
  label,
  className,
  disabled,
  readAloud,
  keys,
  children,
}: {
  control: TouchControl;
  remote: RemoteApi;
  label: string;
  className: string;
  disabled?: boolean;
  readAloud: boolean;
  keys?: string;
  children: ReactNode;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { locale } = useI18n();
  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const down = (e: PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* 합성 이벤트(시험)에는 캡처가 없다 */
    }
    remote.press(e.pointerId, control);
    if (readAloud)
      timer.current = setTimeout(
        () => speak(label, locale === "ko" ? "ko-KR" : "en-US"),
        LONG_PRESS_MS,
      );
  };
  const up = (e: PointerEvent<HTMLButtonElement>) => {
    clearTimer();
    remote.release(e.pointerId);
  };
  // 키보드로 버튼에 포커스해 Space/Enter 를 누르는 경우(스위치 접근) — 누르는 동안만
  const kdown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === " " || e.key === "Enter") && !e.repeat && !disabled) {
      e.preventDefault();
      remote.press(-1, control);
    }
  };
  const kup = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " || e.key === "Enter") remote.release(-1);
  };
  const on = remote.held.has(control);
  return (
    <button
      type="button"
      className={`${className}${on ? " is-held" : ""}`}
      aria-label={label}
      aria-pressed={on}
      aria-keyshortcuts={keys}
      disabled={disabled}
      data-control={control}
      onPointerDown={down}
      onPointerUp={up}
      onPointerCancel={up}
      onLostPointerCapture={up}
      onKeyDown={kdown}
      onKeyUp={kup}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </button>
  );
}

function Slider({ remote, disabled }: { remote: RemoteApi; disabled?: boolean }) {
  const { t } = useI18n();
  const track = useRef<HTMLDivElement>(null);
  const v = remote.slider;
  const fromX = (clientX: number) => {
    const r = track.current?.getBoundingClientRect();
    if (!r || r.width <= 0) return;
    remote.setSlider(((clientX - r.left) / r.width) * 200 - 100);
  };
  const shown = v > 0 ? `+${v}` : String(v);
  return (
    <div
      className="pf-remote__slider"
      data-testid="remote-slider"
      // 패널 전체가 손잡이 — 보이는 막대(16px)보다 넓게 잡는다(SU 터치 타깃 ≥44px)
      onPointerDown={(e) => {
        if (disabled) return;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* 시험 */
        }
        fromX(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons & 1 && !disabled) fromX(e.clientX);
      }}
    >
      <p className="pf-remote__speed" aria-hidden="true">
        {t("remote.speed", { value: shown })}
      </p>
      <div
        ref={track}
        className="pf-remote__track"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={t("remote.sliderLabel")}
        aria-valuemin={-100}
        aria-valuemax={100}
        aria-valuenow={v}
        aria-valuetext={t("remote.speed", { value: shown })}
        aria-disabled={disabled || undefined}
        onKeyDown={(e) => {
          // 슬라이더에 포커스가 있으면 좌우 키는 슬라이더 몫(차는 움직이지 않음)
          const d =
            e.key === "ArrowRight" || e.key === "ArrowUp"
              ? SLIDER_STEP
              : e.key === "ArrowLeft" || e.key === "ArrowDown"
                ? -SLIDER_STEP
                : e.key === "Home"
                  ? -200
                  : e.key === "End"
                    ? 200
                    : 0;
          if (!d || disabled) return;
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          remote.setSlider(v + d);
        }}
      >
        <i className="pf-remote__fill" style={{ width: `${(v + 100) / 2}%` }} />
      </div>
      <p className="pf-remote__caption" aria-hidden="true">
        {t("remote.sliderCaption")}
      </p>
    </div>
  );
}

export function RemoteOverlay({
  variant,
  remote,
  disabled,
}: {
  variant: "se" | "su";
  remote: RemoteApi;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const se = variant === "se";
  const grid = se ? SE_GRID : SU_GRID;
  return (
    <div
      className={`pf-remote pf-remote--${variant}${disabled ? " is-disabled" : ""}`}
      role="group"
      aria-label={t("remote.label")}
      aria-disabled={disabled || undefined}
      data-testid={se ? "fs-09-02" : "fs-09-01"}
    >
      <div className="pf-remote__dpad" role="group" aria-label={t("remote.dpad")}>
        {grid.map((d, i) =>
          d ? (
            <HoldButton
              key={d}
              control={d}
              remote={remote}
              label={t(`remote.dir.${d}`)}
              className="pf-remote__btn pf-remote__btn--dir"
              disabled={disabled}
              readAloud={se}
            >
              <span aria-hidden="true">{GLYPH[d][variant]}</span>
            </HoldButton>
          ) : se ? (
            <span key={`gap-${i}`} className="pf-remote__gap" aria-hidden="true" />
          ) : (
            <span key="center" className="pf-remote__btn pf-remote__btn--center" aria-hidden="true">
              ·
            </span>
          ),
        )}
      </div>
      <div className="pf-remote__right">
        {se ? (
          <div className="pf-remote__speeds" role="radiogroup" aria-label={t("remote.speedGroup")}>
            {(["slow", "fast"] as const).map((k) => (
              <SpeedButton key={k} speed={k} remote={remote} disabled={disabled} />
            ))}
          </div>
        ) : (
          <Slider remote={remote} disabled={disabled} />
        )}
        <HoldButton
          control="a"
          remote={remote}
          label={t("remote.a")}
          className="pf-remote__btn pf-remote__btn--a"
          disabled={disabled}
          readAloud={se}
          keys="Z"
        >
          A
        </HoldButton>
        <HoldButton
          control="b"
          remote={remote}
          label={t("remote.b")}
          className="pf-remote__btn pf-remote__btn--b"
          disabled={disabled}
          readAloud={se}
          keys="X"
        >
          B
        </HoldButton>
      </div>
    </div>
  );
}

function SpeedButton({
  speed,
  remote,
  disabled,
}: {
  speed: "slow" | "fast";
  remote: RemoteApi;
  disabled?: boolean;
}) {
  const { t, locale } = useI18n();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const on = remote.seSpeed === speed;
  const label = t(`remote.${speed}`);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      className={`pf-remote__btn pf-remote__btn--speed${on ? " is-on" : ""}`}
      disabled={disabled}
      data-speed={speed}
      aria-keyshortcuts={speed === "slow" ? "Q" : "E"}
      onClick={() => remote.setSeSpeed(speed)}
      onPointerDown={() => {
        timer.current = setTimeout(
          () => speak(label, locale === "ko" ? "ko-KR" : "en-US"),
          LONG_PRESS_MS,
        );
      }}
      onPointerUp={() => timer.current && clearTimeout(timer.current)}
      onPointerCancel={() => timer.current && clearTimeout(timer.current)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="pf-remote__speed-icon" aria-hidden="true">
        {speed === "slow" ? "🐢" : "🐇"}
      </span>
      {label}
    </button>
  );
}
